import { DataTypeList, Labels, Data2, DataValue2 } from "../../../../../features/data-types"
import { Action, Column, Data, FieldType, FieldValue, SpinnerDiv, ModalId, useUIActions, UIPermission } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { useFind } from "../../../../../state/reducers/hooks"
import { useEffect, useState } from "react"
import { DeliveryRoadmap, Roadmap } from "../../../state/types"
import { useLocation } from "react-router-dom"
import { RoadmapFilters } from "../roadmap-filters/roadmapFilters"
import { PreparationStatus } from "../../../../common"
import { useSystemStateHooks } from "../../../../system"
import { RoutePath } from "../../../../../features/routes"
import { useRoadmapHooks } from "../../../hooks/roadmapHooks"
import { firstItem } from "../../../../../utils/listUtils"
import { Delivery, DeliveryLink, DeliveryOrder, DownloadDeliveryNotesModal } from "../../../../deliveries"

type Props = {
    roadmaps: Roadmap[]
    deliveryRoadmaps: DeliveryRoadmap[]
    shipments: Delivery[]
    shipmentOrders: DeliveryOrder[]
}

export const RoadmapList = (props: Props) => {
    const { roadmaps, deliveryRoadmaps, shipments, shipmentOrders } = props

    const [rows, setRows] = useState<Data2[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)
    const [currentShipments, setCurrentShipments] = useState<Delivery[]>([])
    const [currentShipmentOrders, setCurrentShipmentOrders] = useState<DeliveryOrder[]>([])
    const [completableRoadmapIds, setCompletableRoadmapIds] = useState<string[]>([])
    
    const { userHasPermission } = useSystemStateHooks()
    const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { isRoadmapCompletable } = useRoadmapHooks()
    const { pathname } = useLocation()

    const { openModal } = useUIActions()
    const find = useFind()

    const load = async () => {
        const completableRoadmapIdsList = (await Promise.all(roadmaps.map(async roadmap =>
            await isRoadmapCompletable(roadmap) ? undefined : roadmap.id
        )))
            .filter(roadmapId => roadmapId)
            .map(roadmapId => roadmapId!)
        setCompletableRoadmapIds(completableRoadmapIdsList)
    }

    useEffect(() => {
        load()
    }, [roadmaps])

    const findRoadmap = (id?: string) => roadmaps.find(roadmap => roadmap.id === id)
    const findRoadmapShipments = (roadmapId?: string) => {
        const shipmentIds = deliveryRoadmaps
            .filter(deliveryRoadmap => deliveryRoadmap.roadmapId === roadmapId)
            .map(deliveryRoadmap => deliveryRoadmap.deliveryId)
        return shipments.filter(shipment => shipmentIds.includes(shipment.id))
    }
    const findRoadmapShipmentOrders = (shipments: Delivery[]) => {
        const shipmentOrderIds = shipments.map(shipment => shipment.orderId)
        return shipmentOrders.filter(shipmentOrder => shipmentOrderIds.includes(shipmentOrder.id))
    }

    const setRowsState = (roadmaps: Roadmap[]) => {
        const roadmapsData = roadmaps.map(roadmap => {
            const { ...data } = roadmap
            const roadmapShipments = findRoadmapShipments(roadmap.id)
            return {
                ...data,
                shipments: roadmapShipments
            }
        })
        setRows(roadmapsData)
        setAsyncDataLoaded(true)
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'driverId',
            type: FieldType.TABLE,
            table: TableName.DRIVERS,
            label: 'Conductor',
            sortValue: (value?: FieldValue) => (find(TableName.DRIVERS, value as string) as { name?: string })?.name ?? ''
        },
        {
            name: 'vehicleId',
            type: FieldType.TABLE,
            table: TableName.VEHICLES,
            label: 'Vehículo',
            sortValue: (value?: FieldValue) => (find(TableName.VEHICLES, value as string) as { name?: string })?.name ?? ''
        },
        {
            name: 'shipments',
            type: FieldType.TEXT,
            label: 'Envíos',
            sortable: false,
            render: (value?: DataValue2) => {
                const roadmapShipments = value as Delivery[]
                const multipleShipments = roadmapShipments.length > 1
                const shipment = firstItem(roadmapShipments)!
                return multipleShipments ?
                    <span>{roadmapShipments.length} Envíos</span> :
                    <DeliveryLink delivery={shipment} />
            }
        },
        {
            name: 'preparationStatus',
            type: FieldType.TEXT,
            label: 'Estado de Preparación',
            sortValue: (value?: FieldValue) => Labels.preparationStatusLabel(value as PreparationStatus),
            render: (value?: FieldValue) => Labels.preparationStatusLabel(value as PreparationStatus)
        }
    ]

    const onDownloadDeliveryNotes = (id?: string) => {
        const roadmapShipments = findRoadmapShipments(id)
        setCurrentShipments(roadmapShipments)
        setCurrentShipmentOrders(findRoadmapShipmentOrders(roadmapShipments))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const actions = (id: string): Action[] => {
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: `${pathname}/${id}` }
        ]

        const roadmap = findRoadmap(id)
        const hasDeliveries = findRoadmapShipments(roadmap?.id).length > 0
        const isReady = roadmap?.preparationStatus === PreparationStatus.READY
        const isCompletable = roadmap && !completableRoadmapIds.includes(roadmap.id)

        if (hasDeliveries) {
            actionList.push({ icon: 'download', tooltipLines: ['Descargar', 'Remitos'], kind: 'tertiary', onClick: onDownloadDeliveryNotes })
        }
        if (hasDeliveryPermission && isReady && isCompletable) {
            actionList.push({ icon: 'bag-check-fill', tooltipLines: ['Completar', 'Envío'], href: `${pathname}/${id}/${RoutePath.COMPLETE_DELIVERY}` })
        }

        return actionList
    }

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => setRowsState(dataTypes as Roadmap[])

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <RoadmapFilters
                roadmaps={roadmaps}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows as Data[]}
                    actions={actions}
                    createLabel="Generar Hoja de Ruta"
                />
            )}
            <DownloadDeliveryNotesModal
                deliveries={currentShipments}
                deliveryOrders={currentShipmentOrders}
            />
        </>
    )
}
